import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * useDebounce Hook
 *
 * Debounces a value - delays updating it until after a specified delay
 * has passed without the value changing.
 *
 * Performance optimization for expensive operations (AIF-33)
 *
 * @param {*} value - The value to debounce
 * @param {number} delay - Delay in milliseconds (default: 300ms)
 * @returns {*} The debounced value
 *
 * @example
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearchTerm = useDebounce(searchTerm, 500);
 *
 * useEffect(() => {
 *   // This only runs 500ms after user stops typing
 *   searchAPI(debouncedSearchTerm);
 * }, [debouncedSearchTerm]);
 */
export function useDebounce(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * useDebouncedCallback Hook
 *
 * Returns a debounced version of a callback function.
 * The callback will only execute after the specified delay has passed
 * since the last invocation.
 *
 * @param {Function} callback - The function to debounce
 * @param {number} delay - Delay in milliseconds (default: 300ms)
 * @param {Array} dependencies - Dependencies array (like useCallback)
 * @returns {Function} Debounced callback function
 *
 * @example
 * const handleResize = useDebouncedCallback(() => {
 *   setWindowWidth(window.innerWidth);
 * }, 150);
 *
 * useEffect(() => {
 *   window.addEventListener('resize', handleResize);
 *   return () => window.removeEventListener('resize', handleResize);
 * }, [handleResize]);
 */
export function useDebouncedCallback(callback, delay = 300, dependencies = []) {
  const timeoutRef = useRef(null);

  const debouncedCallback = useCallback(
    (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay, ...dependencies]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
}

/**
 * useWindowResize Hook
 *
 * Efficiently tracks window size with debounced updates.
 * Prevents excessive re-renders during resize.
 *
 * @param {number} delay - Debounce delay in milliseconds (default: 150ms)
 * @returns {Object} { width, height } - Current window dimensions
 *
 * @example
 * function MyComponent() {
 *   const { width, height } = useWindowResize(150);
 *
 *   return <div>Window: {width}x{height}</div>;
 * }
 */
export function useWindowResize(delay = 150) {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const handleResize = useDebouncedCallback(
    () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    },
    delay,
    []
  );

  useEffect(() => {
    // Set initial size
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  return windowSize;
}

/**
 * useWindowWidth Hook
 *
 * Simplified hook for tracking only window width (most common use case).
 * Handles mobile viewport quirks (visual viewport vs. inner width).
 *
 * @param {number} delay - Debounce delay in milliseconds (default: 150ms)
 * @returns {number} Current window width
 *
 * @example
 * function MyComponent() {
 *   const windowWidth = useWindowWidth(150);
 *   const isMobile = windowWidth < 768;
 *
 *   return isMobile ? <MobileNav /> : <DesktopNav />;
 * }
 */
export function useWindowWidth(delay = 150) {
  const [windowWidth, setWindowWidth] = useState(() => {
    // Handle mobile viewport correctly
    if (window.innerWidth < 765) {
      return window.innerWidth;
    }
    return window.visualViewport?.width || window.innerWidth;
  });

  const handleResize = useDebouncedCallback(
    () => {
      const width =
        window.innerWidth < 765
          ? window.innerWidth
          : window.visualViewport?.width || window.innerWidth;
      setWindowWidth(width);
    },
    delay,
    []
  );

  useEffect(() => {
    // Set initial width
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Also listen to visualViewport resize (for mobile)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, [handleResize]);

  return windowWidth;
}

/**
 * Standalone debounce utility function
 *
 * Creates a debounced version of a function that delays invoking func
 * until after delay milliseconds have elapsed since the last time the
 * debounced function was invoked.
 *
 * @param {Function} func - The function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 *
 * @example
 * const debouncedSearch = debounce((query) => {
 *   searchAPI(query);
 * }, 500);
 *
 * // Call multiple times rapidly
 * debouncedSearch('a');
 * debouncedSearch('ab');
 * debouncedSearch('abc'); // Only this call executes after 500ms
 */
export function debounce(func, delay = 300) {
  let timeoutId;

  const debounced = function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };

  // Add cancel method to manually cancel pending invocation
  debounced.cancel = function () {
    clearTimeout(timeoutId);
  };

  return debounced;
}

/**
 * Standalone throttle utility function
 *
 * Creates a throttled version of a function that only invokes func
 * at most once per every limit milliseconds.
 *
 * Difference from debounce: Throttle guarantees execution every X ms,
 * while debounce waits for silence.
 *
 * @param {Function} func - The function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 *
 * @example
 * // For scroll events - execute at regular intervals
 * const throttledScroll = throttle((event) => {
 *   updateScrollPosition(event);
 * }, 100); // Execute at most once per 100ms
 *
 * window.addEventListener('scroll', throttledScroll);
 */
export function throttle(func, limit = 300) {
  let inThrottle;

  return function (...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
}

// Default export with all utilities
export default {
  useDebounce,
  useDebouncedCallback,
  useWindowResize,
  useWindowWidth,
  debounce,
  throttle,
};
